/**
 * Rent guarantee policy slide-over (add / edit)
 *
 * Dispatch 'open-rent-guarantee-slideover' with:
 *   {}              to add a policy, choosing the property first
 *   { propertyId }  to add a policy to a property
 *   { id }          to edit a policy
 *
 * The policy's charge to the landlord is edited as dated lines (From / To,
 * £ fixed or % of rent, monthly or yearly). Each line is one recurring charge
 * on the server; a gap between lines means no charge. A line that has started
 * keeps its start and amount — only its To date can move.
 */

import flatpickr from "flatpickr"

const PICKER = {
  dateFormat: 'Y-m-d',
  altInput: true,
  altFormat: 'j M Y',
  allowInput: false,
  appendTo: document.body
}

let lineKey = 0

function blankLine() {
  return { key: ++lineKey, id: null, from: '', to: '', kind: 'percent', amount: '3', frequency: 'monthly', started: false, current: false }
}

export function rentGuaranteeSlideover() {
  return {
    open: false,
    loading: false,
    saving: false,
    errors: [],
    zIndex: 100010,
    mode: 'create',
    policyId: null,
    policy: null,
    endingPolicy: false,
    endDate: '',
    propertyName: '',
    currentRentPence: null,
    providers: [],
    form: {},
    lines: [],
    documents: [],
    newDocuments: [],
    uploading: false,

    // Choosing the property (adding from the Rent Guarantee page)
    choosingProperty: false,
    propertySearch: '',
    propertyResults: [],

    async handleOpen(detail = {}) {
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) this.zIndex = store.getNextZIndex()
      this.errors = []
      this.newDocuments = []
      this.saving = false
      this.policy = null
      this.endingPolicy = false
      this.endDate = ''
      this.loading = true
      this.open = true
      this.toggleAiFab(false)

      try {
        if (detail.id) {
          this.choosingProperty = false
          this.mode = 'edit'
          this.policyId = detail.id
          const res = await fetch(`/rent-guarantees/${detail.id}.json`, { headers: { Accept: 'application/json' } })
          const data = await res.json()
          this.fill(data.policy)
          this.lines.push(...this.linesFrom(data.unlinked_lines))
          this.providers = data.providers || []
        } else if (!detail.propertyId) {
          this.mode = 'create'
          this.policyId = null
          this.choosingProperty = true
          this.propertySearch = ''
          this.propertyResults = []
          this.propertyName = ''
          this.form = {}
          this.destroyLinePickers()
          this.lines = []
          this.documents = []
          this.$nextTick(() => this.$refs.propertySearchInput?.focus())
        } else {
          this.mode = 'create'
          this.policyId = null
          this.choosingProperty = false
          const res = await fetch(`/rent-guarantees/new.json?property_id=${detail.propertyId}`, { headers: { Accept: 'application/json' } })
          const data = await res.json()
          this.propertyName = data.property_name
          this.currentRentPence = data.current_rent_pence
          this.providers = data.providers || []
          this.form = {
            property_id: data.property_id,
            provider: this.providers.length === 1 ? this.providers[0] : '',
            policy_number: '',
            starts_on: '',
            period_ends_on: '',
            insured_rent: data.current_rent_pence ? (data.current_rent_pence / 100).toFixed(2) : '',
            charge_between_tenancies: false
          }
          this.destroyLinePickers()
          const running = this.linesFrom(data.unlinked_lines)
          this.lines = running.length ? running : [blankLine()]
          this.documents = []
        }
      } catch (e) {
        console.error('Rent guarantee: failed to load', e)
        this.errors = ['Could not load the policy. Please try again.']
      } finally {
        this.loading = false
        this.syncPolicyPickers()
      }
    },

    async searchProperties() {
      const q = this.propertySearch.trim()
      if (q.length < 2) { this.propertyResults = []; return }
      try {
        const res = await fetch(`/api/properties/search?q=${encodeURIComponent(q)}`, { headers: { Accept: 'application/json' } })
        this.propertyResults = await res.json()
      } catch (e) {
        console.error('Rent guarantee: property search failed', e)
      }
    },

    async chooseProperty(property) {
      await this.handleOpen({ propertyId: property.id })
    },

    fill(policy) {
      this.policy = policy
      this.propertyName = policy.property_name
      this.currentRentPence = policy.current_rent_pence
      this.form = {
        property_id: policy.property_id,
        provider: policy.provider || '',
        policy_number: policy.policy_number || '',
        starts_on: policy.starts_on || '',
        period_ends_on: policy.period_ends_on || '',
        insured_rent: policy.insured_rent_pence ? (policy.insured_rent_pence / 100).toFixed(2) : '',
        charge_between_tenancies: !!policy.charge_between_tenancies
      }
      this.destroyLinePickers()
      this.lines = this.linesFrom(policy.lines)
      this.documents = policy.documents || []
    },

    linesFrom(lines) {
      return (lines || []).map(l => ({ ...l, key: ++lineKey, to: l.to || '' }))
    },

    close() {
      this.destroyLinePickers()
      this.open = false
      this.toggleAiFab(true)
    },

    // The AI button sits where the Save button is; the other slideovers hide it too
    toggleAiFab(visible) {
      const fab = document.querySelector('.ai-fab-container')
      if (!fab) return
      fab.style.transition = 'opacity 0.2s ease'
      if (visible) {
        fab.style.display = ''
        requestAnimationFrame(() => { fab.style.opacity = '1' })
      } else {
        fab.style.opacity = '0'
        setTimeout(() => { if (this.open) fab.style.display = 'none' }, 200)
      }
    },

    get title() {
      return this.mode === 'create' ? 'Add rent guarantee' : 'Edit rent guarantee'
    },

    get currentRentDisplay() {
      return this.currentRentPence ? `£${(this.currentRentPence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2 })} pcm` : 'No current tenancy'
    },

    get insuredDiffers() {
      const insured = Math.round(parseFloat(String(this.form.insured_rent).replace(/[£,]/g, '')) * 100)
      return this.currentRentPence && insured && insured !== this.currentRentPence
    },

    // --- Charge lines ---

    addLine() {
      this.lines.push(blankLine())
    },

    removeLine(line) {
      this.$root.querySelectorAll(`[data-line="${line.key}"] [data-picker]`).forEach(el => el._flatpickr?.destroy())
      this.lines = this.lines.filter(l => l.key !== line.key)
    },

    amountHint(line) {
      const period = line.frequency === 'annual' ? 'a year' : 'a month'
      if (line.kind === 'fixed') return `+ VAT ${period}`
      const pct = parseFloat(line.amount)
      if (!this.currentRentPence || !pct) return `% of rent + VAT ${period}`
      const months = line.frequency === 'annual' ? 12 : 1
      const pence = Math.floor(this.currentRentPence * months * pct / 100)
      return `% of rent + VAT ${period} (£${(pence / 100).toFixed(2)} today)`
    },

    lineBadge(line) {
      const today = new Date().toISOString().slice(0, 10)
      if (line.to && line.to < today) return 'Ended'
      if (line.from && line.from <= today && line.id) return 'Charging now'
      return line.id ? 'Not started' : 'New'
    },

    whenText(line) {
      return line.frequency === 'annual' ? 'Charged once a year, on the From date' : 'Charged on each rent due date'
    },

    setKind(line, kind) {
      if (line.started || line.kind === kind) return
      line.kind = kind
      line.amount = kind === 'percent' ? '3' : ''
    },

    // --- Date pickers (flatpickr) ---
    // Each date input sets up its own picker from x-init, so a charge line's
    // pickers exist as soon as the line does. The policy's two pickers live
    // as long as the panel and are re-pointed at each policy as it opens; a
    // line's are destroyed with the line.

    initPicker(el, line = null) {
      if (el._flatpickr) return
      const field = el.dataset.picker
      flatpickr(el, {
        ...PICKER,
        // A started line's From is fixed: charges have been raised from it.
        clickOpens: !(line?.started && field === 'from'),
        defaultDate: (line || this.form)[field] || null,
        onChange: (_dates, dateStr) => {
          const target = line || this.form
          target[field] = dateStr
          if (line && field === 'from' && dateStr) this.joinPreviousLine(line)
          if (!line && field === 'starts_on' && dateStr && !this.form.period_ends_on) {
            // A policy year runs to the day before the anniversary (8 Sep → 7 Sep)
            const d = new Date(dateStr + 'T00:00:00Z')
            d.setUTCFullYear(d.getUTCFullYear() + 1)
            d.setUTCDate(d.getUTCDate() - 1)
            this.form.period_ends_on = d.toISOString().slice(0, 10)
            this.$root.querySelector('[data-picker="period_ends_on"]')?._flatpickr?.setDate(this.form.period_ends_on, false)
          }
        }
      })
    },

    // Lines follow on from each other: the line before this one (by date)
    // ends the day before it starts.
    joinPreviousLine(line) {
      const earlier = this.lines.filter(l => l !== line && l.from && l.from < line.from)
      if (!earlier.length) return
      const previous = earlier.reduce((a, b) => (a.from > b.from ? a : b))
      const d = new Date(line.from + 'T00:00:00Z')
      d.setUTCDate(d.getUTCDate() - 1)
      previous.to = d.toISOString().slice(0, 10)
      this.$root.querySelector(`[data-line="${previous.key}"] [data-picker="to"]`)?._flatpickr?.setDate(previous.to, false)
    },

    // Show the opened policy's dates in the policy pickers.
    syncPolicyPickers() {
      this.$root.querySelectorAll('[data-picker="starts_on"], [data-picker="period_ends_on"]').forEach(el => {
        const fp = el._flatpickr
        if (!fp) return
        const value = this.form[el.dataset.picker]
        value ? fp.setDate(value, false) : fp.clear(false, false)
      })
    },

    destroyLinePickers() {
      this.$root.querySelectorAll('[data-line] [data-picker]').forEach(el => el._flatpickr?.destroy())
    },

    // --- Documents ---

    async addFiles(fileList) {
      const files = Array.from(fileList || [])
      if (!files.length) return
      this.uploading = true
      try {
        const manager = new window.DirectUploadManager({})
        for (const file of files) {
          const signedId = await manager.uploadFile(file)
          this.newDocuments.push({ signedId, filename: file.name })
        }
      } catch (e) {
        console.error('Rent guarantee: upload failed', e)
        this.errors = ['A file could not be uploaded. Please try again.']
      } finally {
        this.uploading = false
      }
    },

    removeNewDocument(doc) {
      this.newDocuments = this.newDocuments.filter(d => d !== doc)
    },

    async deleteDocument(doc) {
      if (!this.policyId) return
      const res = await fetch(`/rent-guarantees/${this.policyId}/documents/${doc.id}.json`, {
        method: 'DELETE',
        headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrfToken }
      })
      if (res.ok) this.documents = this.documents.filter(d => d.id !== doc.id)
    },

    // --- Decisions on the policy ---
    // End it (cover and the landlord's charge stop on that day), keep it past
    // the tenancy that is ending, or record the rent the provider now insures.

    pounds(pence) {
      return '£' + (Number(pence || 0) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    async endPolicy(endedOn) {
      if (!endedOn) return
      const ok = await this.policyAction(`/rent-guarantees/${this.policyId}/end_policy.json`, { ended_on: endedOn }, 'Could not end the policy.')
      if (ok) this.endingPolicy = false
    },

    async keepPolicy() {
      await this.policyAction(`/rent-guarantees/${this.policyId}/keep.json`, {}, 'Could not keep the policy.')
    },

    async confirmInsuredRent(pence) {
      const ok = await this.policyAction(`/rent-guarantees/${this.policyId}.json`, { policy: { insured_rent: (pence / 100).toFixed(2) } }, 'Could not update the insured rent.')
      if (ok) this.form.insured_rent = (pence / 100).toFixed(2)
    },

    async policyAction(url, body, failure) {
      this.errors = []
      try {
        const res = await fetch(url, {
          method: 'PATCH',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken },
          body: JSON.stringify(body)
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok || !data.success) {
          this.errors = data.errors || [failure]
          return false
        }
        this.policy = data.policy
        window.dispatchEvent(new CustomEvent('rent-guarantee-saved', { detail: { policy: data.policy } }))
        return true
      } catch (e) {
        console.error('Rent guarantee: action failed', e)
        this.errors = [failure]
        return false
      }
    },

    // --- Save ---

    async save() {
      if (this.saving || this.uploading || this.choosingProperty) return
      this.saving = true
      this.errors = []
      const body = {
        policy: this.form,
        lines: this.lines.map(l => ({ id: l.id, from: l.from, to: l.to, kind: l.kind, amount: l.amount, frequency: l.frequency })),
        document_signed_ids: this.newDocuments.map(d => d.signedId)
      }
      const url = this.mode === 'create' ? '/rent-guarantees.json' : `/rent-guarantees/${this.policyId}.json`
      try {
        const res = await fetch(url, {
          method: this.mode === 'create' ? 'POST' : 'PATCH',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken },
          body: JSON.stringify(body)
        })
        const data = await res.json()
        if (!res.ok || !data.success) {
          this.errors = data.errors || ['Could not save the policy.']
          return
        }
        window.dispatchEvent(new CustomEvent('rent-guarantee-saved', { detail: { policy: data.policy } }))
        this.close()
      } catch (e) {
        console.error('Rent guarantee: save failed', e)
        this.errors = ['Could not save the policy. Please try again.']
      } finally {
        this.saving = false
      }
    },

    get csrfToken() {
      return document.querySelector('meta[name="csrf-token"]')?.content
    }
  }
}
